<template>
  <!-- Figures at a glance, as a readout: one cell per figure, label above
       the number, colored when it means something (gold needs attention,
       green is done). -->
  <p
    class="kb-stats"
    :class="size === 'sm' ? 'kb-stats-sm' : ''"
  >
    <!-- In reading order ("165 members"), as a screen reader says it -->
    <span
      v-for="item in shown"
      :key="item.label"
      v-tooltip.top="item.hint ? { value: item.hint, showDelay: 300 } : undefined"
      class="kb-stat"
      :class="[item.tone ? `kb-stat-${item.tone}` : '', item.hint ? 'kb-stat-hint' : '']"
      :tabindex="item.hint ? 0 : undefined"
    ><span class="kb-stat-value">{{ display(item.value) }}</span> <span class="kb-stat-label">{{ item.label }}</span></span>
  </p>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export interface KbStat {
  label: string
  value: number | string | null | undefined
  /** ok = green, warn = gold, alert = red, honor = purple; none = plain */
  tone?: 'ok' | 'warn' | 'alert' | 'honor'
  /** Explains the figure, in a tooltip */
  hint?: string
  /** Leave it out (a figure that doesn't apply here) */
  hidden?: boolean
}

const props = withDefaults(defineProps<{
  items: KbStat[]
  /** sm inside drawers and tight list heads, md (default) elsewhere */
  size?: 'sm' | 'md'
}>(), { size: 'md' })

const shown = computed(() => props.items.filter(item => !item.hidden))

function display(value: KbStat['value']): string {
  if (value === null || value === undefined || value === '') return '—'
  return typeof value === 'number' ? value.toLocaleString('en-US') : value
}
</script>

<style scoped>
/* A readout: the figures in one box, a faint green tint so it reads as the
   page's numbers at a glance; one cell per figure, its label above it */
.kb-stats {
  display: inline-grid;
  grid-auto-columns: minmax(max-content, 1fr);
  grid-auto-flow: column;
  max-width: 100%;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--kb-accent) 22%, var(--kb-line));
  border-radius: 4px;
  background: color-mix(in srgb, var(--kb-accent) 4%, var(--kb-raised));
}

/* The value comes first in the markup ("165 members" for a screen reader);
   the label shows above it */
.kb-stat {
  display: flex;
  flex-direction: column-reverse;
  justify-content: flex-end;
  gap: 0.1875rem;
  min-width: 0;
  padding: 0.5rem 1rem 0.5625rem;
  /* Dividers that survive wrapping onto a second row */
  box-shadow: -1px 0 0 color-mix(in srgb, var(--kb-accent) 16%, var(--kb-line)), 0 -1px 0 color-mix(in srgb, var(--kb-accent) 16%, var(--kb-line));
}

.kb-stat-value {
  font: 700 1.25rem/1.2 var(--kb-font-mono);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: var(--kb-fg);
}

.kb-stat-label {
  font: 500 10.5px/1.3 var(--kb-font-mono);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
  color: var(--kb-dim);
}

.kb-stats-sm .kb-stat {
  padding: 0.375rem 0.75rem 0.4375rem;
}

.kb-stats-sm .kb-stat-value {
  font-size: 1rem;
}

/* A figure that means something: its color, and a line of it on top */
.kb-stat-ok { --kb-stat-tone: var(--kb-accent); }
.kb-stat-warn { --kb-stat-tone: var(--kb-gold); }
.kb-stat-alert { --kb-stat-tone: var(--kb-alert); }
.kb-stat-honor { --kb-stat-tone: var(--kb-honor); }

.kb-stat-ok,
.kb-stat-warn,
.kb-stat-alert,
.kb-stat-honor {
  box-shadow: -1px 0 0 color-mix(in srgb, var(--kb-accent) 16%, var(--kb-line)), inset 0 2px 0 var(--kb-stat-tone);
}

.kb-stat-ok .kb-stat-value,
.kb-stat-warn .kb-stat-value,
.kb-stat-alert .kb-stat-value,
.kb-stat-honor .kb-stat-value {
  color: var(--kb-stat-tone);
}

/* A figure with an explanation: dotted underline on the label */
.kb-stat-hint .kb-stat-label {
  text-decoration: underline dotted var(--kb-line-2);
  text-underline-offset: 3px;
  cursor: help;
}

/* Phones: two to a row */
@media (max-width: 639px) {
  .kb-stats {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-auto-flow: row;
  }
}
</style>
